import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Spinner, Button } from 'react-bootstrap';
import Select from 'react-select';
import BoxContainer from '~src/components/BoxContainer';
import { onErrorShowMessage } from '~src/errors';
import ProjectItem from '../ProjectItem/ProjectItem';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  fetchEditorProjects,
  fetchEditorProjectTypes,
  fetchFuneralServiceById,
} from '~graphql/controllers';
import styles from './ProjectsPage.module.scss';
import useQueryString from '../useQueryString';
import { FuneralServicesSelectorModal } from '~src/components/FuneralServices';

const ProjectPage = () => {
  const urlParams = new URLSearchParams(window.location.search);

  const [funeralService, setFuneralService] = useState();
  const [isLoadingCanvases, setIsLoadingCanvases] = useState(true);
  const [isLoadingProjectTypes, setIsLoadingProjectTypes] = useState(true);
  const [
    showFuneralServiceSelectorModal,
    setShowFuneralServiceSelectorModal,
  ] = React.useState(false);
  const [projectTypes, setProjectTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [after, setAfter] = useState('');
  const [filterProjectType, setFilterProjectType] = useQueryString(
    urlParams,
    'projectType'
  );
  const [filterFuneralService, setFilterFuneralService] = useQueryString(
    urlParams,
    'funeralService'
  );

  const parseTypesResult = (items) =>
    items.map((item) => ({
      value: item.objectId,
      label: item.name,
      isGlobal: item.isGlobal,
    }));

  // Required when it comes from loader
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (filterFuneralService) {
      fetchFuneralServiceById(filterFuneralService).then(
        (selectedFuneralService) => {
          setFuneralService(selectedFuneralService);
        }
      );
    }
  }, [filterFuneralService]);

  useEffect(() => {
    fetchEditorProjectTypes()
      .then((result) => {
        setProjectTypes(parseTypesResult(result));
        setIsLoadingProjectTypes(false);
      })
      .catch((err) => {
        console.error(err);
        onErrorShowMessage();
      });
  }, []);

  const fetchAllProjects = useCallback(() => {
    const filters = {};

    if (filterProjectType) {
      const [objectId, isGlobal = false] = filterProjectType.split('_');

      filters.filterProjectType = {
        objectId,
        isGlobal: JSON.parse(isGlobal), // trick to cast string to bool
      };
    }

    if (filterFuneralService) {
      filters.filterFuneralService = filterFuneralService;
    }

    fetchEditorProjects({ after, filters })
      .then((result) => {
        let canvases = [...items, ...result.edges];

        canvases = canvases.map((item) => {
          const pages = item.editor_canvas_pages.edges
            ? item.editor_canvas_pages.edges
            : item.editor_canvas_pages;
          item.editor_canvas_pages = pages.sort(
            (a, b) => a.node.order - b.node.order
          );
          return item;
        });

        setItems(canvases);
        setAfter(result.pageInfo.endCursor);
        setHasMore(result.pageInfo.hasNextPage);
        setIsLoadingCanvases(false);
      })
      .catch((err) => {
        console.error(err);
        onErrorShowMessage();
      });
  }, [after, filterProjectType, filterFuneralService]);

  useEffect(() => {
    fetchAllProjects();
  }, [filterProjectType, filterFuneralService]);

  let selectedProjectTypeId;
  if (filterProjectType) {
    selectedProjectTypeId = filterProjectType?.split('_')[0];
  }

  const selectedProjectType = projectTypes.find(
    (item) => item.value === selectedProjectTypeId
  );

  return (
    <Container fluid>
      <BoxContainer
        className="mx-1 my-4"
        titleClassName="mb-4"
        xsColSize={12}
        xsColOffset={0}
        smColSize={12}
        smColOffset={0}
        lgColSize={12}
        lgColOffset={0}
        theme="light"
        title="I tuoi progetti"
        spacing={false}
      >
        <Row className={`${styles.filterBox} my-1 p-3 pb-4`}>
          <Col xs={12}>
            <h3 className="text-dark text-left my-3">Filtri</h3>
          </Col>
          <Col xs={12} sm={6} md={6} lg={4} xl={4}>
            <Select
              isLoading={isLoadingProjectTypes}
              className="basic-single mt-2"
              name="selectProjectType"
              id="selectProjectType"
              placeholder="Seleziona tipologia di progetto"
              options={projectTypes}
              value={
                selectedProjectType
                  ? selectedProjectType
                  : 'Seleziona tipologia di progetto'
              }
              onChange={(e) => {
                if (e && e.value) {
                  setItems([]);
                  setAfter('');
                  setIsLoadingCanvases(true);
                  setFilterProjectType(`${e.value}_${e.isGlobal || false}`);
                }
              }}
              styles={{
                menu: (provided) => ({
                  ...provided,
                  color: 'black',
                }),
              }}
            />
          </Col>
          <Col xs={12} sm={6} md={6} lg={4} xl={4}>
            <Button
              variant="primary"
              className="btn-block mt-2"
              onClick={() => {
                setShowFuneralServiceSelectorModal(true);
              }}
            >
              {funeralService && filterFuneralService
                ? [funeralService.nome, funeralService.cognome].join(' ')
                : 'Cerca Servizio'}
            </Button>
            <FuneralServicesSelectorModal
              show={showFuneralServiceSelectorModal}
              onHide={() => setShowFuneralServiceSelectorModal(false)}
              onChange={(e) => {
                if (e && e.objectId) {
                  setItems([]);
                  setAfter('');
                  setIsLoadingCanvases(true);
                  setFilterFuneralService(e.objectId);
                  setFuneralService(e);
                  setShowFuneralServiceSelectorModal(false);
                }
              }}
            />
          </Col>
          <Col xs={12} sm={12} md={12} lg={3} xl={2}>
            <Button
              variant="light"
              disabled={!filterProjectType && !filterFuneralService}
              className="btn-block mt-2"
              onClick={() => {
                setFilterFuneralService('');
                setFilterProjectType('');
                setItems([]);
                setAfter('');
              }}
            >
              Rimuovi filtri
            </Button>
          </Col>
        </Row>
        <InfiniteScroll
          dataLength={items.length}
          next={() => fetchAllProjects()}
          hasMore={hasMore}
          loader={
            <Col xs={12} className="text-center">
              <Spinner className="mt-2" animation="grow" variant="info" />
            </Col>
          }
        >
          <Container fluid>
            <Row
              className={
                isLoadingCanvases || items.length == 0 ? 'text-center' : ''
              }
              style={{ minHeight: '50vh' }}
            >
              {isLoadingCanvases && (
                <Col xs={12}>
                  <h3 className="text-dark" style={{ marginTop: '15vh' }}>
                    Stiamo caricando i tuoi progetti...
                  </h3>
                </Col>
              )}
              {!isLoadingCanvases && items.length == 0 && (
                <Col xs={12}>
                  <h3 className="text-dark" style={{ marginTop: '15vh' }}>
                    Nessun progetto trovato. Vuoi crearne uno?
                  </h3>
                  <Button
                    className="mt-2"
                    href={`${process.env.ROUTE_START}?${
                      selectedProjectTypeId
                        ? `tipo=${selectedProjectTypeId}&`
                        : ''
                    }${
                      filterFuneralService
                        ? `servizio=${filterFuneralService}`
                        : ''
                    }`}
                  >
                    Crealo ora
                  </Button>
                </Col>
              )}
              {!isLoadingCanvases &&
                items.map((project) => {
                  return (
                    <ProjectItem
                      key={project.objectId}
                      objectId={project.objectId}
                      name={`${project.defunto_pointer?.nome} ${project.defunto_pointer?.cognome}`}
                      metadata={project.metadata}
                      pages={project.editor_canvas_pages?.map(
                        (page) => page.node
                      )}
                      gifPreviewId={
                        project.editor_canvas_pages[0]?.node.gifPreviewId
                      }
                    />
                  );
                })}
            </Row>
          </Container>
        </InfiniteScroll>
      </BoxContainer>
    </Container>
  );
};

export default ProjectPage;
